import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useToast } from '../contexts/ToastContext';
import { STRIPE_CONFIG } from '../config/stripe';
import * as StripeService from '../services/stripeService';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { colors, typography, spacing, borderRadius, shadows, components } from '../styles/theme';

const PaymentModal = ({ visible, onClose, event, userId, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const { showToast } = useToast();

  const handlePayment = async () => {
    if (!event || !userId) {
      showToast('Missing event or user information', 'error');
      return;
    }

    if (!event.club?.stripeAccountId) {
      showToast('This club has not set up payments yet', 'error');
      return;
    }

    try {
      setLoading(true);

      // Create payment intent
      const { clientSecret, paymentIntentId } = await StripeService.createEventPaymentIntent(
        event.id,
        userId,
        event.price,
        event.club.stripeAccountId,
        event.stripeFeePercentage || STRIPE_CONFIG.platformFeePercentage
      );

      setLoading(false);
      setPaymentProcessing(true);

      // Process payment using Stripe Payment Sheet
      const result = await StripeService.processEventPayment(
        event,
        userId,
        clientSecret
      );

      if (result.success) {
        showToast('Payment successful! You are registered.', 'success');
        onPaymentSuccess?.(paymentIntentId, event.price);
        onClose();
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToast(error.message || 'Payment failed. Please try again.', 'error');
    } finally {
      setLoading(false);
      setPaymentProcessing(false);
    }
  };

  if (!event) return null;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.header}>
            <Text style={styles.title}>Complete Payment</Text>
            <TouchableOpacity onPress={onClose} disabled={loading || paymentProcessing}>
              <Icon name="close" size={24} color={colors.text.primary} />
            </TouchableOpacity>
          </View>

          <View style={styles.eventInfo}>
            <Text style={styles.eventTitle}>{event.title}</Text>
            {event.club?.clubName && (
              <Text style={styles.clubName}>by {event.club.clubName}</Text>
            )}
          </View>

          <View style={styles.paymentDetails}>
            <View style={styles.priceRow}>
              <Text style={styles.priceLabel}>Event Fee:</Text>
              <Text style={styles.priceAmount}>${event.price?.toFixed(2)}</Text>
            </View>
            {event.stripeFeePercentage && (
              <View style={styles.priceRow}>
                <Text style={styles.feeNote}>
                  Platform fee ({event.stripeFeePercentage}%)
                </Text>
                <Text style={styles.feeAmount}>
                  ${((event.price * event.stripeFeePercentage) / 100).toFixed(2)}
                </Text>
              </View>
            )}
            <View style={[styles.priceRow, styles.totalRow]}>
              <Text style={styles.totalLabel}>Total:</Text>
              <Text style={styles.totalAmount}>${event.price?.toFixed(2)}</Text>
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.payButton, (loading || paymentProcessing) && styles.payButtonDisabled]}
              onPress={handlePayment}
              disabled={loading || paymentProcessing}
            >
              {loading || paymentProcessing ? (
                <ActivityIndicator color={colors.background.primary} />
              ) : (
                <>
                  <Icon name="payment" size={20} color={colors.background.primary} />
                  <Text style={styles.payButtonText}>
                    Pay ${event.price?.toFixed(2)}
                  </Text>
                </>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
              disabled={loading || paymentProcessing}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.securityNote}>
            🔒 Your payment is secure and encrypted
          </Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.background.primary,
    borderTopLeftRadius: borderRadius.xl,
    borderTopRightRadius: borderRadius.xl,
    padding: spacing.lg,
    paddingBottom: spacing.xl,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    ...typography.h2,
    color: colors.text.primary,
  },
  eventInfo: {
    marginBottom: spacing.lg,
  },
  eventTitle: {
    ...typography.h3,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  clubName: {
    ...typography.body,
    color: colors.text.secondary,
  },
  paymentDetails: {
    backgroundColor: colors.background.secondary,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  priceLabel: {
    ...typography.body,
    color: colors.text.secondary,
  },
  priceAmount: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
  },
  feeNote: {
    ...typography.caption,
    color: colors.text.secondary,
  },
  feeAmount: {
    ...typography.caption,
    color: colors.text.secondary,
  },
  totalRow: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  totalLabel: {
    ...typography.h3,
    color: colors.text.primary,
  },
  totalAmount: {
    ...typography.h3,
    fontWeight: '700',
    color: colors.primary.main,
  },
  buttonContainer: {
    marginBottom: spacing.md,
  },
  payButton: {
    ...components.button.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  payButtonDisabled: {
    opacity: 0.6,
  },
  payButtonText: {
    ...typography.button,
    color: colors.background.primary,
    marginLeft: spacing.sm,
  },
  cancelButton: {
    ...components.button.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButtonText: {
    ...typography.button,
    color: colors.text.secondary,
  },
  securityNote: {
    ...typography.caption,
    textAlign: 'center',
    color: colors.text.tertiary,
    marginTop: spacing.md,
  },
});

export default PaymentModal;

